import { Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import deleteColumn from 'components/elements/table/editable-columns/delete-column';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetGuestColumn from 'hooks/use-get-guest-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { addNewGuestItem } from '../../hooks/use-get-default-values';
import {
  HotelPurchaseGuestItemFormType,
  HotelPurchaseFormType,
} from '../form-type';

interface Props {
  parentIndex: number;
}

function useGetScheduleGuestTableList(): IColumn<HotelPurchaseGuestItemFormType>[] {
  const { t } = useTranslation();
  const { editable } = useFormState();
  const guestColumn = useGetGuestColumn();

  return [
    {
      header: t('common:lead_guest'),
      accessorKey: 'isLead',
      minSize: 88,
      size: 88,
      dataType: ColumnDataType.checkbox,
      disabled: true,
    },
    {
      header: t('common:guest'),
      accessorKey: 'guestId',
      minSize: 180,
      dataType: ColumnDataType.select,
      required: true,
      selectProps: guestColumn,
    },
    ...deleteColumn(editable),
  ];
}

export default function ScheduleGuestTable(props: Props) {
  const { editable } = useFormState();
  const name =
    `hotelPurchases.${props.parentIndex}.hotelPurchaseGuests` as const;

  const { clearErrors, control, getValues, trigger } =
    useFormContext<HotelPurchaseFormType>();

  const { update, append, remove } = useFieldArray({
    control,
    name,
    keyName: 'customId',
  });

  const columns = useGetScheduleGuestTableList();

  const dispatch = React.useCallback(
    (action) => {
      const guests = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const guest = guests[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof HotelPurchaseGuestItemFormType;
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<HotelPurchaseGuestItemFormType> = () => {
        if (columnId === 'guestId') {
          return {
            guestName: extra?.[0]?.label as string,
          };
        }
        return {};
      };

      if (type === 'addRow') {
        append(addNewGuestItem());
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...guest,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(name);
      }
    },
    [append, clearErrors, getValues, name, remove, trigger, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={getValues(name)}
        dispatch={dispatch}
        disabled={!editable}
        name={name}
        uniqueRowKey="customId"
      />
    </div>
  );
}
